import { Fragment, useState, useEffect } from "react";
import {
  Box, Button, TextField, Typography, Paper,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Grid,
} from "@mui/material";
import { clientsApi, ordersApi } from "../services/api";
import SearchBar from "../components/common/SearchBar";
import StatusBadge from "../components/common/StatusBadge";
import TablePager from "../components/common/TablePager";
import { ORDER_STATUS_LABELS } from "../utils/constants";
import useCrudForm from "../hooks/useCrudForm";
import { useNotification } from "../context/NotificationContext";
import { APP_PALETTE } from "../theme/palette";
import { formatCurrencyBs, formatDateEsBo } from "../utils/formatters";

export default function ClientsPage() {
  const { notifyWarning } = useNotification();
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expanded, setExpanded] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const load = () => {
    clientsApi.list().then((r) => setClients(r.data));
    ordersApi.list().then((r) => setOrders(r.data));
  };
  useEffect(() => { load(); }, []);

  const {
    showForm,
    editing,
    form,
    setForm,
    toggleCreate,
    openEdit,
    submitForm,
  } = useCrudForm({
    initialForm: { full_name: "", phone: "", address: "", delivery_city: "", delivery_department: "", notes: "" },
    loadData: load,
    createItem: (payload) => clientsApi.create(payload),
    updateItem: (id, payload) => clientsApi.update(id, payload),
    mapToForm: (c) => ({
      full_name: c.full_name,
      phone: c.phone || "",
      address: c.address || "",
      delivery_city: c.delivery_city || "",
      delivery_department: c.delivery_department || "",
      notes: c.notes || "",
    }),
    validateCreate: (payload) => {
      if (!payload.phone.trim()) {
        notifyWarning("El teléfono es obligatorio para registrar cliente");
        return false;
      }
      return true;
    },
  });

  useEffect(() => {
    if (!showForm) {
      setSubmitAttempted(false);
    }
  }, [showForm]);

  const getClientOrders = (clientId) => orders.filter((o) => o.client_id === clientId);

  const filtered = clients.filter((c) =>
    !search ||
    c.full_name.toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || "").includes(search)
  ).sort((a, b) => b.id - a.id);

  const getClientAddress = (c) => {
    const addr = c.address || "";
    if (addr.toLowerCase().startsWith("otra ciudad/departamento")) {
      const parts = addr.split(" - ").map(p => p.trim());
      const city = parts.find((p, i) => i > 0 && !p.toLowerCase().startsWith("transporte:"));
      return city || c.delivery_city || "—";
    }
    return addr || c.delivery_city || "—";
  };

  const handleSubmit = () => {
    setSubmitAttempted(true);
    submitForm();
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color={APP_PALETTE.text.primary}>Clientes</Typography>
        <Button variant="contained" onClick={toggleCreate}
          sx={{ width: { xs: "100%", sm: "auto" }, background: APP_PALETTE.brand.primary, "&:hover": { background: APP_PALETTE.brand.primaryHover }, borderRadius: 2 }}>
          {showForm ? "Cancelar" : "+ Nuevo cliente"}
        </Button>
      </Box>

      {showForm && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)" }}>
          <Typography variant="h6" fontWeight={600} mb={2}>{editing ? "Editar cliente" : "Registrar cliente"}</Typography>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={6}>
              <TextField label={submitAttempted ? "Nombre completo *" : "Nombre completo"} size="small" fullWidth
                value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={submitAttempted ? "Teléfono / WhatsApp *" : "Teléfono / WhatsApp"} size="small" fullWidth
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+591 7..." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Dirección" size="small" fullWidth
                value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Ciudad de entrega" size="small" fullWidth
                value={form.delivery_city} onChange={(e) => setForm({ ...form, delivery_city: e.target.value })} placeholder="La Paz" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Departamento de entrega" size="small" fullWidth
                value={form.delivery_department} onChange={(e) => setForm({ ...form, delivery_department: e.target.value })} placeholder="Chuquisaca" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Notas" size="small" fullWidth
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.full_name || (!editing && !form.phone.trim())}
            sx={{ width: { xs: "100%", sm: "auto" }, background: APP_PALETTE.brand.primary, "&:hover": { background: APP_PALETTE.brand.primaryHover }, borderRadius: 2 }}>
            {editing ? "Guardar cambios" : "Registrar"}
          </Button>
        </Paper>
      )}

      <SearchBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        resultCount={filtered.length}
      />

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)", overflowX: "auto" }}>
          <Table sx={{ minWidth: { xs: 980, md: "auto" } }}>
            <TableHead>
              <TableRow sx={{ background: APP_PALETTE.surfaces.subtle }}>
                {["Nombre", "Teléfono", "Dirección", "Tipo de entrega", "Transportes", "Notas", "Pedidos", "Acciones"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: APP_PALETTE.text.muted, fontSize: 13 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c) => {
                const clientOrders = getClientOrders(c.id);
                const isExpanded = expanded[c.id];
                return (
                  <Fragment key={c.id}>
                    <TableRow hover>
                      <TableCell sx={{ fontWeight: 600 }}>{c.full_name}</TableCell>
                      <TableCell sx={{ color: APP_PALETTE.text.secondary }}>{c.phone || "—"}</TableCell>
                      <TableCell sx={{ color: APP_PALETTE.text.secondary }}>{getClientAddress(c)}</TableCell>
                      <TableCell>
                        {c.delivery_mode === "other_city" ? (
                          <Typography variant="caption" sx={{ background: "#fff3e0", color: "#e65100", borderRadius: 1, px: 1, py: 0.25, fontWeight: 600, fontSize: 12 }}>
                            📦 Envío — {c.delivery_city || "otra ciudad"}
                          </Typography>
                        ) : c.delivery_mode === "same_city" ? (
                          <Typography variant="caption" sx={{ background: "#e8f5e9", color: "#2e7d32", borderRadius: 1, px: 1, py: 0.25, fontWeight: 600, fontSize: 12 }}>
                            🏪 Local — {c.delivery_city || "misma ciudad"}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: APP_PALETTE.text.muted }}>— sin definir</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: APP_PALETTE.text.secondary }}>
                        {(c.delivery_transport_companies || []).length > 0
                          ? c.delivery_transport_companies.join(", ")
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ color: APP_PALETTE.text.muted, fontSize: 13 }}>{c.notes || "—"}</TableCell>
                      <TableCell>
                        {clientOrders.length > 0 ? (
                          <Button size="small" variant="outlined"
                            onClick={() => setExpanded({ ...expanded, [c.id]: !isExpanded })}
                            sx={{ color: APP_PALETTE.brand.primary, borderColor: APP_PALETTE.brand.primary, borderRadius: 2, fontSize: 12 }}>
                            {isExpanded ? "▲ Ocultar" : `▼ ${clientOrders.length} pedido(s)`}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Sin pedidos</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => openEdit(c)}
                          sx={{ color: APP_PALETTE.brand.primary, borderColor: APP_PALETTE.surfaces.brandBorderSoft, background: APP_PALETTE.brand.soft, borderRadius: 2, fontSize: 13 }}>
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isExpanded && clientOrders.map((o) => {
                      const s = ORDER_STATUS_LABELS[o.status] || { label: o.status, color: APP_PALETTE.text.muted };
                      return (
                        <TableRow key={`order-${o.id}`} sx={{ background: APP_PALETTE.surfaces.subtle }}>
                          <TableCell sx={{ pl: 4, color: APP_PALETTE.text.secondary, fontSize: 13 }}>└ Pedido #{o.id}</TableCell>
                          <TableCell sx={{ color: APP_PALETTE.text.muted, fontSize: 12 }}>
                            {formatDateEsBo(o.created_at, { day: "numeric", month: "short", year: "numeric" })}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: APP_PALETTE.brand.primary, fontSize: 13 }}>{formatCurrencyBs(o.total)}</TableCell>
                          <TableCell colSpan={4}><StatusBadge label={s.label} color={s.color} /></TableCell>
                          <TableCell />
                        </TableRow>
                      );
                    })}
                  </Fragment>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: APP_PALETTE.text.muted }}>
                    {search ? "No se encontraron clientes" : "No hay clientes registrados"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePager
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(0); }}
          />
        </TableContainer>
      </Box>

      {/* Vista móvil: cards */}
      <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.5 }}>
        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c) => {
          const clientOrders = getClientOrders(c.id);
          const isExpanded = expanded[c.id];
          return (
            <Paper key={c.id} sx={{ p: 2, borderRadius: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", sm: "flex-start" }, flexDirection: { xs: "column", sm: "row" }, gap: 1, mb: 1 }}>
                <Box>
                  <Typography fontWeight={700} fontSize={15}>{c.full_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.phone || "Sin teléfono"}</Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => openEdit(c)}
                  sx={{ width: { xs: "100%", sm: "auto" }, color: APP_PALETTE.brand.primary, borderColor: APP_PALETTE.surfaces.brandBorderSoft, background: APP_PALETTE.brand.soft, borderRadius: 2, fontSize: 12 }}>
                  Editar
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  📍 {getClientAddress(c) || "Sin dirección"}
                </Typography>

                <Box>
                  {c.delivery_mode === "other_city" ? (
                    <Typography variant="caption" sx={{ background: "#fff3e0", color: "#e65100", borderRadius: 1, px: 1, py: 0.25, fontWeight: 600, fontSize: 12 }}>
                      📦 Envío — {c.delivery_city || "otra ciudad"}
                    </Typography>
                  ) : c.delivery_mode === "same_city" ? (
                    <Typography variant="caption" sx={{ background: "#e8f5e9", color: "#2e7d32", borderRadius: 1, px: 1, py: 0.25, fontWeight: 600, fontSize: 12 }}>
                      🏪 Local — {c.delivery_city || "misma ciudad"}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">Sin modo de entrega</Typography>
                  )}
                </Box>

                {(c.delivery_transport_companies || []).length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    🚚 {c.delivery_transport_companies.join(", ")}
                  </Typography>
                )}

                {c.notes && (
                  <Typography variant="caption" color="text.secondary">
                    📝 {c.notes}
                  </Typography>
                )}
              </Box>

              {clientOrders.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Button size="small" variant="outlined" fullWidth
                    onClick={() => setExpanded({ ...expanded, [c.id]: !isExpanded })}
                    sx={{ color: APP_PALETTE.brand.primary, borderColor: APP_PALETTE.brand.primary, borderRadius: 2, fontSize: 12 }}>
                    {isExpanded ? "▲ Ocultar pedidos" : `▼ Ver ${clientOrders.length} pedido(s)`}
                  </Button>
                  {isExpanded && (
                    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                      {clientOrders.map((o) => {
                        const s = ORDER_STATUS_LABELS[o.status] || { label: o.status, color: APP_PALETTE.text.muted };
                        return (
                          <Box key={o.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1, borderRadius: 2, background: APP_PALETTE.surfaces.subtle }}>
                            <Box>
                              <Typography variant="caption" fontWeight={600}>Pedido #{o.id}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatDateEsBo(o.created_at, { day: "numeric", month: "short", year: "numeric" })}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="caption" fontWeight={600} color={APP_PALETTE.brand.primary}>
                                {formatCurrencyBs(o.total)}
                              </Typography>
                              <StatusBadge label={s.label} color={s.color} />
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}

        {filtered.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ py: 3, textAlign: "center", display: "block" }}>
            {search ? "No se encontraron clientes" : "No hay clientes registrados"}
          </Typography>
        )}

        <TablePager
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(0); }}
        />
      </Box>
    </Box>
  );
}